import { DropDownList } from '@syncfusion/ej2-dropdowns';
import { TextBox } from '@syncfusion/ej2-inputs';

import { DOCUMENT_DOWNLOAD_MAX_SIZE_MB, DOCUMENT_UPLOAD_MAX_SIZE_MB, SIGN_REQUEST_MAX_FILES } from '@/shared/constants';
import { routes } from '@/shared/routes';
import { RequestType } from '@/shared/types/enum';

import { execute } from '@/front/actions/utils';
import { getControl } from '@/front/editable/state';
import { listen } from '@/front/event';
import { POST } from '@/front/fetch';
import { alertModal } from '@/front/modal';
import { setupTempAttachments } from '@/front/utils/attachments';
import { getEditableDiv, getEditableTr, getInput, getInputTr, setVisible } from '@/front/utils/dom';

listen('editablesReady', () => {
  const form = document.querySelector('form#request') as HTMLFormElement | null;

  if (form) {
    const typeEditable = getEditableDiv(form, 'type');
    const typeDropDownList = getControl(typeEditable!.id) as DropDownList;

    const envelopeBalance = document.getElementById('envelopeBalance') as HTMLSpanElement;

    const titleEditable = getEditableDiv(form, 'title');
    const titleTextBox = getControl(titleEditable!.id) as TextBox;

    const individualIdTr = getEditableTr(form, 'individualId');

    const requiresApprovalTr = getEditableTr(form, 'requiresApproval');

    const formIdTr = getEditableTr(form, 'formId');

    const downloadFilesTr = getInputTr(form, 'downloadsJson');
    const downloadsJsonInput = getInput(form, 'downloadsJson');
    const downloadFileInput = document.getElementById('downloadFileInput') as HTMLInputElement | null;
    const downloadFilesList = document.getElementById('downloadFilesList');

    const signerIndividualIdsTr = getEditableTr(form, 'signerIndividualIds');
    const signerUserIdsTr = getEditableTr(form, 'signerUserIds');
    const boldsignDocumentTitleTr = getEditableTr(form, 'boldsignDocumentTitle');
    const boldsignDocumentsFilesTr = getInputTr(form, 'boldsignDocumentsJson');
    const boldsignDocumentsJsonInput = getInput(form, 'boldsignDocumentsJson');
    const boldsignDocumentsFileInput = document.getElementById('boldsignDocumentsFileInput') as HTMLInputElement | null;
    const boldsignDocumentsFilesList = document.getElementById('boldsignDocumentsFilesList');

    const attachmentsManager =
      downloadFileInput && downloadFilesList
        ? setupTempAttachments({
            fileInput: downloadFileInput,
            list: downloadFilesList,
            maxSizeMb: DOCUMENT_DOWNLOAD_MAX_SIZE_MB,
            tempUrl: routes.requests.downloadTempUrl(),
            onError: alertModal,
            onChange: (items) => {
              if (downloadsJsonInput) downloadsJsonInput.value = JSON.stringify(items);
            },
          })
        : undefined;

    const signAttachmentsManager =
      boldsignDocumentsFileInput && boldsignDocumentsFilesList
        ? setupTempAttachments({
            fileInput: boldsignDocumentsFileInput,
            list: boldsignDocumentsFilesList,
            maxSizeMb: DOCUMENT_UPLOAD_MAX_SIZE_MB,
            maxFiles: SIGN_REQUEST_MAX_FILES,
            accept: ['application/pdf'],
            tempUrl: routes.requests.downloadTempUrl(),
            onError: alertModal,
            onChange: (items) => {
              if (boldsignDocumentsJsonInput) boldsignDocumentsJsonInput.value = JSON.stringify(items);
            },
          })
        : undefined;

    const refresh = () => {
      const formSelected = typeDropDownList.value === RequestType.Form;
      const uploadSelected = typeDropDownList.value === RequestType.Upload;
      const downloadSelected = typeDropDownList.value === RequestType.Download;
      const signSelected = typeDropDownList.value === RequestType.Sign;

      setVisible(formIdTr, formSelected);

      setVisible(downloadFilesTr, downloadSelected);
      setVisible(requiresApprovalTr, downloadSelected);

      setVisible(envelopeBalance, signSelected);
      setVisible(individualIdTr, !signSelected);
      setVisible(signerIndividualIdsTr, signSelected);
      setVisible(signerUserIdsTr, signSelected);
      setVisible(boldsignDocumentTitleTr, signSelected);
      setVisible(boldsignDocumentsFilesTr, signSelected);

      const defaultMessages = [
        'Please complete this form',
        'e.g. Please upload your ... documents',
        'e.g. Please download your ... document',
        'Please sign this document',
      ];

      if (!titleTextBox.value || defaultMessages.includes(titleTextBox.value)) {
        if (formSelected) {
          titleTextBox.value = titleTextBox.placeholder = defaultMessages[0];
        } else if (uploadSelected) {
          titleTextBox.value = '';
          titleTextBox.placeholder = defaultMessages[1];
        } else if (downloadSelected) {
          titleTextBox.value = '';
          titleTextBox.placeholder = defaultMessages[2];
        } else if (signSelected) {
          titleTextBox.value = titleTextBox.placeholder = defaultMessages[3];
        }
      }
    };

    refresh();

    typeDropDownList.addEventListener('change', () => {
      refresh();
    });

    if (form && (downloadsJsonInput || boldsignDocumentsJsonInput)) {
      form.addEventListener('submit', (e) => {
        const downloadSelected = typeDropDownList.value === RequestType.Download;
        const signSelected = typeDropDownList.value === RequestType.Sign;

        if (downloadSelected && (attachmentsManager?.getItems().length ?? 0) === 0) {
          e.preventDefault();
          alertModal('Please add at least one file.');
          return;
        }

        if (signSelected && (signAttachmentsManager?.getItems().length ?? 0) === 0) {
          e.preventDefault();
          alertModal('Please add at least one file.');
          return;
        }
      });
    }

    const copyUploadLinks = document.querySelectorAll<HTMLAnchorElement>('[data-copy-request-upload="true"]');

    for (const link of copyUploadLinks) {
      link.addEventListener('click', async (event) => {
        event.preventDefault();

        if (link.getAttribute('data-copy-request-upload') !== 'true') {
          return;
        }

        const href = link.getAttribute('href');

        if (!href) {
          return;
        }

        link.classList.add('disabled');
        link.setAttribute('aria-disabled', 'true');

        try {
          const { res, status } = await execute(POST, href);

          if (res.ok) {
            link.classList.remove('text-primary');
            link.classList.add('text-muted');
            link.removeAttribute('data-copy-request-upload');
            link.setAttribute('href', '#');
            link.setAttribute('title', 'Already copied');
            link.setAttribute('data-bs-original-title', 'Already copied');
            return;
          }

          if (!status?.message) {
            alertModal('Could not copy file. Please try again.');
          }
        } finally {
          if (link.getAttribute('data-copy-request-upload') === 'true') {
            link.classList.remove('disabled');
            link.removeAttribute('aria-disabled');
          }
        }
      });
    }
  }
});
